import type { ApplicationState, Job } from '../../types/tracked-job';

export type PipelineColumnKey = 'applied' | 'interviewing' | 'offer' | 'rejected' | 'hired';

function appliedAtMs(job: Job): number {
  const raw = (job as { appliedAt?: string }).appliedAt;
  if (!raw || typeof raw !== 'string') return 0;
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : 0;
}

/**
 * Groups applied jobs into pipeline columns (mutually exclusive applicationState; null → "Applied").
 * Within each column, most recently applied first.
 */
export function groupAppliedJobsByPipelineColumn(jobs: Job[]): Record<PipelineColumnKey, Job[]> {
  const buckets: Record<PipelineColumnKey, Job[]> = {
    applied: [],
    interviewing: [],
    offer: [],
    rejected: [],
    hired: [],
  };

  for (const job of jobs) {
    const state = job.applicationState ?? null;
    if (state === 'interviewing') {
      buckets.interviewing.push(job);
    } else if (state === 'offer') {
      buckets.offer.push(job);
    } else if (state === 'rejected') {
      buckets.rejected.push(job);
    } else if (state === 'hired') {
      buckets.hired.push(job);
    } else {
      buckets.applied.push(job);
    }
  }

  const sortDesc = (a: Job, b: Job) => appliedAtMs(b) - appliedAtMs(a);
  (Object.keys(buckets) as PipelineColumnKey[]).forEach((k) => {
    buckets[k].sort(sortDesc);
  });

  return buckets;
}

export function pipelineColumnKeyForState(state: ApplicationState): PipelineColumnKey {
  if (state === 'interviewing') return 'interviewing';
  if (state === 'offer') return 'offer';
  if (state === 'rejected') return 'rejected';
  if (state === 'hired') return 'hired';
  return 'applied';
}
